// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {
    DefaultButton,
    SpinnerSize,
} from 'office-ui-fabric-react';

import {
    deviceInformationSelector,
    deviceSelector
} from './../../../store/deviceInfo/selectors';

import {
    allSelectedOfferSelector,
    deviceRepairSelector,
    replacePartsSelector,
    isFullDeviceRepairSelector,
    progressSelector,
    shippingOfferSelector,
    deviceShippingOfferSelector,
    reActivateOrderStatusSelector,
} from './../../../store/orderCreation/selectors';
import {
    GET_SHIPPING_ACCESSORY_OFFER_DATA,
    GET_SHIPPING_DEVICE_OFFER_DATA,
    getShippingAddressData,
    Progress,
    updateCreateOrderProgress,
} from './../../../store/orderCreation';

import { safeTrim } from 'src/lib/stringUtil';

import { LoadingState } from './../../../store/actionStatus';
import BusySpinner from './../../../components/BusySpinner';

import ConfirmSerialReplacement from './../../../components/ConfirmSerialReplacement/ConfirmSerialReplacement';

import { userInfoSelector } from './../../../store/user/selector';

import Styles from './styles.module.scss';
import {
    containsWalkInCenterOffer,
    defaultToDatabaseShipToAddress,
    isShippingAddressRequired,
} from 'src/helpers/shippingChecks';

import { logPageAction } from 'src/lib/ApplicationInsights';

function isLoading(): boolean {
    const accessoryShippingOfferLoading = useSelector((state: any) => state.actionStatus[GET_SHIPPING_ACCESSORY_OFFER_DATA] || {});
    const deviceShippingOfferLoading = useSelector((state: any) => state.actionStatus[GET_SHIPPING_DEVICE_OFFER_DATA] || {});
    return deviceShippingOfferLoading.status === LoadingState.started || accessoryShippingOfferLoading.status === LoadingState.started;
}

function isFormSubmittable(
    isFullDeviceReplacement: boolean,
    replaceParts: any,
    selectedPartsWithRegex: any,
    deviceSerial: string
): boolean {
    // Validate if each part has a replacement
    if (isFullDeviceReplacement) {
        return !!replaceParts[deviceSerial];
    }
    return Object.keys(replaceParts).length === selectedPartsWithRegex.length
        && selectedPartsWithRegex.every((part: any) => (replaceParts[part.serialNumber]));
}

interface PropType {
    setBypassSerialReplacement: (val: boolean) => void;
}

export default function ReplacementNewSerialNumber(props: PropType) {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const device = useSelector(state => deviceSelector(state));
    const deviceInformation = useSelector(state => deviceInformationSelector(state));
    const reActivateOrder = useSelector(state =>     reActivateOrderStatusSelector(state));
    const deviceData = {
        serialNumber: device.serialNumber,
        name: deviceInformation.repairProduct.description,
        imageUri: deviceInformation.repairProduct.imageUri,
    };

    const isFullDeviceReplacement = useSelector(state => isFullDeviceRepairSelector(state));

    const replaceParts = useSelector(state => replacePartsSelector(state) || {});

    const allDeviceItems = useSelector(state => deviceRepairSelector(state));
    const selectedOffers = useSelector(state => allSelectedOfferSelector(state));
    const deviceShippingOffer = useSelector(state => deviceShippingOfferSelector(state));

    const selectedPartsWithRegex = Object.keys(selectedOffers)
        .filter(key => Object.keys(selectedOffers[key]).length > 0)
        .filter(key => selectedOffers[key].isReplacementSerialNumberRequired)
        .map((key: string) => ({
            imageUrl: selectedOffers[key].imageUrl || { value: '' },
            serialNumber: key,
            ...allDeviceItems[key],
        }));
    const currentStage: Progress = useSelector(state => progressSelector(state));

    const shippingOffers = useSelector(state => shippingOfferSelector(state));
    const userInfo = useSelector(state => userInfoSelector(state));

    if (isLoading()) {
        return (<BusySpinner
            className={Styles.spinner}
            messageKey={t('loading')}
            size={SpinnerSize.large}
        />);
    }

    if (!showReplacementSerialNumber(selectedOffers, deviceShippingOffer, allDeviceItems, reActivateOrder)) {
        if (currentStage === Progress.UpdateDeviceNewSerialNumber) {
            props.setBypassSerialReplacement(true);
            if (isShippingAddressRequired(shippingOffers, selectedOffers)) {
                dispatch(
                    updateCreateOrderProgress(Progress.AddingShippingAddress)
                );
            }
            else {
                createShippingDefaultAddress();
                dispatch(updateCreateOrderProgress(Progress.Registration));
            }
        }
        return null;
    }

    function onSubmit() {
        logPageAction('Continue button for replacement serial number clicked');

        if (currentStage === Progress.UpdateDeviceNewSerialNumber) {
            if (defaultToDatabaseShipToAddress(shippingOffers, selectedOffers)) {
                createShippingDefaultAddress();
            }
            if (isShippingAddressRequired(shippingOffers, selectedOffers)) {
                dispatch(updateCreateOrderProgress(Progress.AddingShippingAddress));
            }
            else {
                createShippingDefaultAddress();
                dispatch(updateCreateOrderProgress(Progress.Registration));
            }
        }
    }

    const isContinueDisabled = !isFormSubmittable(isFullDeviceReplacement, replaceParts, selectedPartsWithRegex, device.serialNumber);

    const btnClassName = clsx(Styles.continueButton, {
        [Styles.disabled]: isContinueDisabled,
    });

    function createShippingDefaultAddress() {
        const language = 'en-US';
        const {
            email,
            phone: phoneNumber,
            shiptoCustomerName: firstName,
            shiptoCustomerName: lastName,
            addressLineOneText: address1,
            addressLineTwoText: address2,
            addressLineThreeText: address3,
            cityName: city,
            postalCode,
            stateProvinceCode: stateOrProvince,
            countryCode: country } = userInfo;

        dispatch(getShippingAddressData({
            email: safeTrim(email),
            firstName: safeTrim(firstName),
            lastName: safeTrim(lastName),
            phoneNumber: safeTrim(phoneNumber),
            language: safeTrim(language),
            address1: safeTrim(address1),
            address2: safeTrim(address2),
            address3: safeTrim(address3),
            city: safeTrim(city),
            postalCode: safeTrim(postalCode),
            stateOrProvince: safeTrim(stateOrProvince),
            country: safeTrim(country),
        }));
    }

    return (
        <div id="replacement_sn" className={Styles.serialNumberContainer}>
            <header className={Styles.headerSection}>
                <h2 className={Styles.h2Heading}>
                    {t('update_device_with_new_serial_number')}
                </h2>
            </header>
            <RenderReplacementSection
                isFullDeviceReplacement={isFullDeviceReplacement}
                device={deviceData}
                selectedPartsWithRegex={selectedPartsWithRegex}
            />
            <section className={Styles.continueButtonContainer}>
                {(currentStage === Progress.UpdateDeviceNewSerialNumber) && <DefaultButton
                    className={btnClassName}
                    text={t('continue')}
                    disabled={isContinueDisabled}
                    onClick={onSubmit}
                />
                }
            </section>
        </div>
    );
}

function RenderReplacementSection({
    isFullDeviceReplacement,
    device,
    selectedPartsWithRegex,
}: any) {
    if (isFullDeviceReplacement) {
        return (
            <section className={Styles.confirmationContainer} >
                <ConfirmSerialReplacement
                    deviceName={device.name}
                    serialNumber={device.serialNumber}
                    image={device.imageUri}
                />
            </section>
        );
    }
    return selectedPartsWithRegex.map((part: any, key: number) => {
        return (
            <section className={Styles.confirmationContainer} key={key}>
                <ConfirmSerialReplacement
                    deviceName={part.name}
                    serialNumber={part.serialNumber}
                    image={part.imageUrl.value}
                    regex={part.regex}
                />
            </section>
        );
    });
}

function showReplacementSerialNumber(selectedOffers: any, shippingOffer: any, allDeviceItems: any, reActivateOrder:boolean): boolean {
    const containsWic = containsWalkInCenterOffer(shippingOffer);
    const replacementSnRequired = Object.keys(selectedOffers).some((key: string) =>
        selectedOffers[key].isReplacementSerialNumberRequired
    );
    return !containsWic && (replacementSnRequired && (!isOnDemandInventorySelected(allDeviceItems, reActivateOrder) && !isCruPartSelected(allDeviceItems)));
}

function isOnDemandInventorySelected(allDeviceItems: any, reActivateOrder:boolean): boolean {
    if(reActivateOrder){
        return false;
    }
    const onDemandInventory = Object.keys(allDeviceItems).filter((key) => allDeviceItems[key].onDemandFru === true);
    if(onDemandInventory.length === 0){
        return false;
    }
    return true;
}

function isCruPartSelected(allDeviceItems: any): boolean {
    const cruParts = Object.keys(allDeviceItems).filter((key) => allDeviceItems[key].isCruOffer === true);
    if(cruParts.length === 0){
        return false;
    }
    return true;
}